/**
 * Stripe Service
 * Handles payment processing and subscription management with Stripe
 * 
 * NOTE: This is a stub implementation. Full Stripe integration requires:
 * 1. Stripe publishable key in environment variables
 * 2. Backend API endpoints for secure checkout session creation
 * 3. Webhook handlers for subscription events
 * 4. Stripe customer and subscription management
 */

import { auth } from '../../firebase/config';

const DEFAULT_BASE_URL = 'https://us-central1-gla-platform-2024.cloudfunctions.net/userManagement';
const functionsBaseUrl = process.env.REACT_APP_FUNCTIONS_BASE_URL || DEFAULT_BASE_URL;

const buildUrl = (path) => {
  const base = functionsBaseUrl.replace(/\/$/, '');
  const cleanPath = path.startsWith('/') ? path : `/${path}`;
  return `${base}${cleanPath}`;
};

const callStripeEndpoint = async (path, body) => {
  const currentUser = auth.currentUser;
  if (!currentUser) {
    throw new Error('User must be signed in to manage subscriptions');
  }

  const token = await currentUser.getIdToken();
  const response = await fetch(buildUrl(path), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(body),
  });

  let payload = null;
  try {
    payload = await response.json();
  } catch (error) {
    throw new Error('Invalid response from subscription service');
  }

  if (!response.ok) {
    const message = payload?.error || `Request failed with status ${response.status}`;
    const err = new Error(message);
    err.status = response.status;
    err.data = payload;
    throw err;
  }

  return payload;
};

/**
 * Create Stripe checkout session
 * @param {string} userId - User ID (unused, kept for compatibility)
 * @param {string} tier - Subscription tier
 * @param {string} interval - Billing interval (only monthly supported)
 * @param {Object} options
 * @param {string} options.successUrl
 * @param {string} options.cancelUrl
 * @returns {Promise<string>} Checkout session URL
 */
export const createCheckoutSession = async (
  userId,
  tier,
  interval = 'monthly',
  options = {},
) => {
  try {
    if (interval !== 'monthly') {
      throw new Error('Only monthly billing is supported at this time');
    }

    const origin = window?.location?.origin || '';
    const {
      successUrl = `${origin}/subscription/film?checkout=success`,
      cancelUrl = `${origin}/subscription/film?checkout=cancelled`,
    } = options;

    const payload = await callStripeEndpoint('/stripe/checkout-session', {
      tier,
      successUrl,
      cancelUrl,
    });

    if (!payload?.url) {
      throw new Error('Checkout session URL was not returned');
    }

    return payload.url;
  } catch (error) {
    console.error('Error creating checkout session:', error);
    throw error;
  }
};

/**
 * Create customer portal session
 * @param {string} returnUrl - URL users return to after managing billing
 * @returns {Promise<string>} Portal session URL
 */
export const createPortalSession = async (returnUrl) => {
  try {
    const payload = await callStripeEndpoint('/stripe/customer-portal-session', {
      returnUrl,
    });

    if (!payload?.url) {
      throw new Error('Billing portal URL was not returned');
    }

    return payload.url;
  } catch (error) {
    console.error('Error creating portal session:', error);
    throw error;
  }
};

/**
 * Cancel subscription
 * @param {string} subscriptionId - Stripe subscription ID
 * @returns {Promise<void>}
 */
export const cancelSubscription = async (subscriptionId) => {
  try {
    console.log(`Cancelling subscription ${subscriptionId}`);
    throw new Error('Direct cancellation by subscriptionId is not supported');
  } catch (error) {
    console.error('Error cancelling subscription:', error);
    throw error;
  }
};
/**
 * Schedule cancellation at period end for the current user's subscription
 * @returns {Promise<Object>} Subscription summary
 */
export const cancelSubscriptionAtPeriodEnd = async () => {
  try {
    const payload = await callStripeEndpoint('/stripe/cancel-subscription', {});
    return payload.subscription;
  } catch (error) {
    console.error('Error scheduling subscription cancellation:', error);
    throw error;
  }
};

/**
 * Resume a previously scheduled cancellation
 * @returns {Promise<Object>} Subscription summary
 */
export const resumeSubscription = async () => {
  try {
    const payload = await callStripeEndpoint('/stripe/resume-subscription', {});
    return payload.subscription;
  } catch (error) {
    console.error('Error resuming subscription:', error);
    throw error;
  }
};

/**
 * Get subscription details
 * @param {string} subscriptionId - Stripe subscription ID
 * @returns {Promise<Object>} Subscription details
 */
export const getSubscription = async (subscriptionId) => {
  try {
    // TODO: Implement subscription retrieval
    console.log(`Fetching subscription ${subscriptionId}`);
    
    // Placeholder - would call backend API
    /*
    const response = await fetch(`/api/stripe/subscription/${subscriptionId}`);
    const data = await response.json();
    return data;
    */
    
    throw new Error('Stripe integration not yet implemented');
  } catch (error) {
    console.error('Error fetching subscription:', error);
    throw error;
  }
};

export default {
  createCheckoutSession,
  createPortalSession,
  cancelSubscription,
  cancelSubscriptionAtPeriodEnd,
  resumeSubscription,
  getSubscription
};

